// AppCajaReactTable.jsx
import React, { useState, useEffect, useMemo, useCallback } from "react";
import styled from "styled-components";
import { useFilters, useSortBy, usePagination } from "react-table";
import { deleteCaja, putCaja, getCajas } from "../helpers/rutaCajas";
import ModalFormCaja from "../components/ModalFormCaja";
import Table from "./TableContainer";
import "../css/tablecomandas.css";

const Styles = styled.div`
  .table-container { overflow: auto; height: 600px; }
  table { border: 1px solid black; border-spacing: 0; font-size: 13px; }
  th, td { padding: 0.5rem; border-bottom: 1px solid black; border-right: 1px solid black; }
  th { background-color: #778899; position: sticky; top: 0; z-index: 2; text-align: center; font-size: 12px; }
  td { background-color: white; }
  .pagination { padding: 0.5rem; background-color: #778899; font-size: 15px; font-weight: bold; }
`;

const InformeCajasHeader = styled.div`
  display: flex;
  align-items: center;

  min-height: 78px;

  margin-bottom: 12px;

  padding: 14px 18px;

  border: 1px solid #ded7f4;
  border-radius: 14px;

  background:
    linear-gradient(
      120deg,
      #ffffff 0%,
      #faf8ff 65%,
      #f1edff 100%
    );

  box-shadow:
    0 3px 14px
    rgba(15, 23, 42, 0.035);

  .header-icon {
    width: 46px;
    height: 46px;

    flex: 0 0 46px;

    display: flex;
    align-items: center;
    justify-content: center;

    margin-right: 13px;

    border-radius: 11px;

    background: #f0ebff;

    color: #7657d8;

    font-size: 19px;
  }

  .header-text span {
    display: block;

    margin-bottom: 2px;

    color: #7657d8;

    font-size: 9px;
    font-weight: 700;

    letter-spacing: 1.7px;
  }

  .header-text h3 {
    margin: 0 0 3px 0;

    color: #18243a;

    font-size: 20px;
    font-weight: 700;
  }

  .header-text p {
    margin: 0;

    color: #748196;

    font-size: 10px;
  }

  .header-count {
    margin-left: auto;

    padding: 7px 12px;

    border: 1px solid #e3def3;
    border-radius: 20px;

    background: #ffffff;

    color: #6953b0;

    font-size: 10px;
    font-weight: 600;
  }

  @media (max-width: 768px) {
    padding: 12px;

    .header-count {
      display: none;
    }

    .header-icon {
      width: 42px;
      height: 42px;

      flex-basis: 42px;
    }

    .header-text h3 {
      font-size: 17px;
    }
  }
`;

const $ARS = (n) =>
  (Number(n) || 0).toLocaleString("es-AR", { style: "currency", currency: "ARS" });

export default function AppCajaReactTable() {
  const [data, setData]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [cajaEdit, setCajaEdit] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Cargar SOLO desde /cajas (el backend ya filtra visibilidad: Pablo ve todo, resto ve públicas + propias)
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const cx = await getCajas(); // { ok, cajas:[...] }
      const list = (cx?.cajas || [])
        .filter(m => m.activo !== false) // Solo activas
        .map((m) => ({
          _id: String(m._id),
          codcaj: m.codcaj,
          descripcion: m.descripcion,
          montoInicial: Number(m.montoInicial || 0),
          saldoActual: Number(m.saldoActual || 0),
          fecha: m.fecha || null,
          activo: m.activo !== false,
        }))
        .sort(
          (a, b) =>
            new Date(b.fecha || 0) - new Date(a.fecha || 0) ||
            Number(b.codcaj || 0) - Number(a.codcaj || 0)
        );

      setData(list);
    } catch (e) {
      console.error("Error cargando cajas:", e);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const borrar = useCallback(async (_id) => {
    if (!window.confirm("¿Eliminar caja?")) return;
    try {
      await deleteCaja(_id);
    } finally {
      fetchData();
    }
  }, [fetchData]);

  const onSave = useCallback(async (id, values) => {
    try {
      await putCaja(id, values);
    } finally {
      setShowModal(false);
      setCajaEdit(null);
      fetchData();
    }
  }, [fetchData]);

const columns = useMemo(() => [
  {
    id: "editar",
    Header: "Edita",
    accessor: "_id",
    width: 60,
    Cell: ({ row }) => (
      <button
        className="action-btn action-btn--edit"
        title="Editar"
        onClick={() => { setCajaEdit(row.original); setShowModal(true); }}
      >
        ✏️
      </button>
    ),
  },
  {
    id: "eliminar",
    Header: "🗑️",
    accessor: "_id",
    width: 60,
    Cell: ({ row }) => (
      <button
        className="action-btn action-btn--del"
        title="Eliminar"
        onClick={() => borrar(row.original._id)}
      >
        🗑️
      </button>
    ),
  },

  { Header: "Nro", accessor: "codcaj", width: 70 },

  {
    Header: "Fecha",
    accessor: (r) => (r.fecha ? new Date(r.fecha) : null),
    id: "fecha",
    width: 130,
    Cell: ({ value }) =>
      value instanceof Date
        ? value.toLocaleDateString("es-AR", { day: "2-digit", month: "2-digit", year: "numeric" })
        : "",
  },

  { Header: "Descripción", accessor: "descripcion", width: 420 },

  // ⬇️ NUEVA COLUMNA EN EL MEDIO
  {
    Header: "Monto Inicial",
    accessor: "montoInicial",
    width: 150,
    Cell: ({ value }) => (
      <div style={{ textAlign: "right" }}>
        {value != null ? $ARS(value) : ""}
      </div>
    ),
    Footer: (info) => {
      const total = info.rows.reduce((s, r) => s + (r.values.montoInicial || 0), 0);
      return (
        <div style={{ textAlign: "right", fontWeight: "bold" }}>
          {$ARS(total)}
        </div>
      );
    },
  },

  {
    Header: "Saldo Actual",
    accessor: "saldoActual",
    width: 150,
    Cell: ({ value }) => (
      <div style={{ textAlign: "right" }}>
        {value != null ? $ARS(value) : ""}
      </div>
    ),
    Footer: (info) => {
      const total = info.rows.reduce((s, r) => s + (r.values.saldoActual || 0), 0);
      return (
        <div style={{ textAlign: "right", fontWeight: "bold", color: "red" }}>
          {$ARS(total)}
        </div>
      );
    },
  },

  {
    Header: "Verde + / Rojo -",
    accessor: (r) => (Number(r.saldoActual || 0) - Number(r.montoInicial || 0)),
    id: "diferencia",
    width: 150,
    Cell: ({ value }) => (
      <div style={{ textAlign: "right", color: value < 0 ? "red" : "green" }}>
        {value != null
          ? value.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
          : ""}
      </div>
    ),
    // ✅ usa los valores de las columnas visibles
    Footer: (info) => {
      const total = info.rows.reduce(
        (s, r) => s + ((r.values.saldoActual || 0) - (r.values.montoInicial || 0)),
        0
      );
      return (
        <div style={{ textAlign: "right", fontWeight: "bold", color: total < 0 ? "red" : "green" }}>
          {total.toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </div>
      );
    },
  },
], [borrar]);

  return (
    <div className="container mt-4">

    <InformeCajasHeader>
      <div className="header-icon">
        <i
          className="fa fa-money"
          aria-hidden="true"
        />
      </div>

      <div className="header-text">
        <span>
          INFORMES · TESORERÍA
        </span>

        <h3>
          Listado de Cajas
        </h3>

        <p>
          Consulta de cajas, montos iniciales,
          saldos actuales y variaciones.
        </p>
      </div>
    </InformeCajasHeader>

    <Styles>
        <div className="table-container">
          <Table
            columns={columns}
            data={data}
            loading={loading}
            plugins={[useFilters, useSortBy, usePagination]}
          />
        </div>
      </Styles>

      {cajaEdit && (
        <ModalFormCaja
          show={showModal}
          handleClose={() => { setShowModal(false); setCajaEdit(null); fetchData(); }}
          caja={cajaEdit}
          onSave={onSave}
        />
      )}
    </div>
  );
}
